import { HTMLAttributes, forwardRef, ReactNode } from "react";
import { cn } from "@/lib/utils";

// =====================
// Card Component
// =====================

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
	hover?: boolean;
	padding?: "none" | "sm" | "md" | "lg";
	variant?: "default" | "bordered" | "elevated";
}

const Card = forwardRef<HTMLDivElement, CardProps>(
	({ className, hover, padding = "md", variant = "default", ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn(
					"rounded-lg bg-white dark:bg-gray-900 transition-all duration-200",
					variant === "default" && "border border-gray-200 dark:border-gray-800",
					variant === "bordered" && "border-2 border-gray-200 dark:border-gray-700",
					variant === "elevated" && "shadow-md dark:shadow-gray-900/50",
					padding === "none" && "",
					padding === "sm" && "p-3",
					padding === "md" && "p-4",
					padding === "lg" && "p-6",
					hover && "hover:shadow-lg hover:border-brand-200 dark:hover:border-brand-800 cursor-pointer",
					className
				)}
				{...props}
			/>
		);
	}
);

Card.displayName = "Card";

// =====================
// Card Sub-components
// =====================

// =====================
// Card Header Props
// =====================

interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
	title?: any;
	description?: string;
	action?: React.ReactNode;
}

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
	({ className, title, description, action, children, ...props }, ref) => {
		return (
			<div ref={ref} className={cn("flex flex-col space-y-1.5", className)} {...props}>
				<div className={cn("flex items-center justify-between", action && "mb-2")}>
					<div className="flex flex-col space-y-1.5">
						{title && (typeof title === 'string' ? 
							<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3> : 
							<>{title}</>
						)}
						{description && <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>}
					</div>
					{action && <div className="ml-auto">{action}</div>}
				</div>
				{children}
			</div>
		);
	}
);

CardHeader.displayName = "CardHeader";

const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
	return <div ref={ref} className={cn("pt-4", className)} {...props} />;
});

CardContent.displayName = "CardContent";

const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
	return <div ref={ref} className={cn("flex items-center pt-4 border-t border-gray-200 dark:border-gray-800", className)} {...props} />;
});

CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardContent, CardFooter };